import { CELL, GROUND } from '../core/config';
import type { Building, Cell, City, Interior, Place } from './city';
import { furnish } from './furniture';
import { roomClearance } from './interior-clearance';

/** A represented room is assembled from actual floor-parented fixtures. Wide
 * door openings keep the existing public corridor and vertical access usable. */
export function prepareRoomLayouts(city: City): void {
  let serial = Math.max(...[...city.buildings, ...(city.props ?? [])].map((b) => b.id)) + 1;
  const props = (city.props ??= []);
  for (const interior of city.interiors ?? []) {
    const building = city.buildings.find((b) => b.id === interior.buildingId)!;
    interior.rooms = [];
    for (const floor of interior.floors) {
      const floorY = floor * interior.floorHeight,
        y = (building.baseY ?? GROUND) + (floorY + 1) * CELL,
        places = city.places!.filter((p) => p.buildingId === building.id && p.floor === floor),
        fixtures = props.filter(
          (p) =>
            p.parentCell !== undefined &&
            Math.floor(p.parentCell / 1e6) === building.id &&
            Math.abs((p.baseY ?? 0) - y) < 0.1,
        ),
        floorCells = new Map(building.cells.filter((c) => c.y === floorY).map((c) => [`${c.x},${c.z}`, c]));
      const room = (
        name: string,
        x: number,
        z: number,
        w: number,
        d: number,
      ): NonNullable<Interior['rooms']>[number] => {
        const record = {
          id: `room:${building.id}:${floor}:${interior.rooms!.length}`,
          floor,
          name,
          x: building.x + x * CELL,
          z: building.z + z * CELL,
          w: w * CELL,
          d: d * CELL,
          places: [] as string[],
          fixtures: [] as number[],
        };
        interior.rooms!.push(record);
        return record;
      };
      const overlaps = (item: Building, padding = 0.06): boolean => {
        const c = item.cells[0],
          bounds = c.bounds!;
        const a = {
          x: item.x + bounds.x * CELL,
          z: item.z + bounds.z * CELL,
          w: bounds.w * CELL,
          d: bounds.d * CELL,
        };
        if (
          fixtures.some((other) =>
            other.cells.some((cell) =>
              (cell.parts ?? [cell.bounds ?? { x: 0, y: 0, z: 0, w: 1, h: 1, d: 1 }]).some((part) => {
                const x = other.x + (cell.x + part.x) * CELL,
                  z = other.z + (cell.z + part.z) * CELL,
                  gap =
                    item.furniture === 'partition' && other.furniture === 'partition' ? -0.00001 : padding;
                return (
                  a.x < x + part.w * CELL + gap &&
                  a.x + a.w > x - gap &&
                  a.z < z + part.d * CELL + gap &&
                  a.z + a.d > z - gap
                );
              }),
            ),
          )
        )
          return true;
        return places.some((p) => {
          // Reserve the complete approach-to-furniture path, including a lying
          // person's longitudinal body rather than only its root marker.
          const points = [{ x: p.x, z: p.z }];
          if (p.pose)
            points.push(
              { x: p.pose.x, z: p.pose.z },
              { x: p.pose.x - Math.sin(p.pose.yaw) * 1.6, z: p.pose.z - Math.cos(p.pose.yaw) * 1.6 },
            );
          const minX = Math.min(...points.map((v) => v.x)) - 0.4,
            maxX = Math.max(...points.map((v) => v.x)) + 0.4,
            minZ = Math.min(...points.map((v) => v.z)) - 0.4,
            maxZ = Math.max(...points.map((v) => v.z)) + 0.4;
          return a.x < maxX && a.x + a.w > minX && a.z < maxZ && a.z + a.d > minZ;
        });
      };
      const add = (
        x: number,
        z: number,
        furniture: Building['furniture'],
        panel?: { vertical: boolean },
      ): Building | undefined => {
        const support = floorCells.get(`${Math.floor(x)},${Math.floor(z)}`);
        if (!support) return;
        const id = serial++,
          cell: Cell = {
            id: id * 1e6,
            x: 0,
            y: 0,
            z: 0,
            hp: 0.85,
            material: 'wood',
            role: 'prop',
            color: 0xd4d0bf,
          },
          item: Building = {
            id,
            name: `${building.name} · ${furniture} on level ${floor + 1}`,
            kind: 'furniture',
            category: 'prop',
            furniture,
            x: building.x + x * CELL,
            z: building.z + z * CELL,
            baseY: y,
            w: 1,
            d: 1,
            h: 2,
            color: cell.color,
            accent: 0x7c8980,
            cells: [cell],
            parentCell: support.id,
            chunk: building.chunk,
          };
        if (panel) {
          cell.bounds = {
            x: panel.vertical ? -0.048 : 0,
            y: 0,
            z: panel.vertical ? 0 : -0.048,
            w: panel.vertical ? 0.096 : 1,
            h: 1.6,
            d: panel.vertical ? 1 : 0.096,
          };
          cell.parts = [{ ...cell.bounds, color: (building.id + floor) % 2 ? 0xd2cfbf : 0xc3ccc2 }];
        } else furnish(cell, furniture!, building.id + floor);
        if (overlaps(item)) return;
        // Do not install a fixture through an existing column, facade or core.
        const bounds = cell.bounds!;
        if (
          building.cells.some((c) => {
            if (c.y <= floorY || c.y >= floorY + interior.floorHeight) return false;
            const s = c.bounds ?? { x: 0, y: 0, z: 0, w: 1, h: 1, d: 1 };
            return (
              x + bounds.x < c.x + s.x + s.w &&
              x + bounds.x + bounds.w > c.x + s.x &&
              z + bounds.z < c.z + s.z + s.d &&
              z + bounds.z + bounds.d > c.z + s.z &&
              c.y + s.y < floorY + 1 + bounds.h
            );
          })
        )
          return;
        props.push(item);
        fixtures.push(item);
        return item;
      };
      const divided = building.w >= 9 && building.d >= 8 && interior.use !== 'workshop',
        xs = [1],
        zs = [1];
      if (divided) {
        for (let x = 4.15; x < building.w - 3; x += interior.use === 'apartment' ? 6 : 9) {
          xs.push(x);
          for (let z = 1; z < building.d - 1; z++) {
            if (z % 3 === 1 || (z === interior.liftZ && Math.abs(x - interior.liftX) < 2)) continue;
            add(x, z, 'partition', { vertical: true });
          }
        }
        if (interior.use === 'apartment' || building.kind === 'mall')
          for (let z = 4.15; z < building.d - 3; z += 6) {
            zs.push(z);
            for (let x = 1; x < building.w - 1; x++) {
              if (x % 3 === 1 || (x === interior.liftX && Math.abs(z - interior.liftZ) < 2)) continue;
              add(x, z, 'partition', { vertical: false });
            }
          }
      }
      xs.push(building.w - 1);
      zs.push(building.d - 1);
      for (let i = 1; i < xs.length; i++)
        for (let j = 1; j < zs.length; j++) {
          const x = xs[i - 1],
            z = zs[j - 1],
            w = xs[i] - x,
            d = zs[j] - z;
          const occupants = places.filter(
            (p) =>
              p.x >= building.x + x * CELL &&
              p.x < building.x + (x + w) * CELL &&
              p.z >= building.z + z * CELL &&
              p.z < building.z + (z + d) * CELL,
          );
          if (!occupants.length && !floorCells.has(Math.floor(x + w / 2) + ',' + Math.floor(z + d / 2)))
            continue;
          const name =
            interior.use === 'workshop'
              ? 'Materials and workshop'
              : interior.use === 'apartment'
                ? occupants.some((p) => p.pose?.posture === 'lying')
                  ? 'Bedroom and living suite'
                  : 'Sitting room'
                : interior.use === 'shop'
                  ? occupants.some((p) => p.kind === 'counter')
                    ? 'Shop and checkout'
                    : 'Retail room'
                  : occupants.some((p) => p.kind === 'seat')
                    ? 'Meeting and break room'
                    : 'Work room';
          room(name, x, z, w, d);
        }
      if (interior.use === 'apartment' || interior.use === 'office') {
        const clear = roomClearance(building, fixtures, floor, interior.floorHeight),
          origin = places[0],
          queue = origin
            ? [[Math.floor((origin.x - building.x) / CELL), Math.floor((origin.z - building.z) / CELL)]]
            : [],
          connected = new Set(queue.map(([x, z]) => `${x},${z}`));
        for (let i = 0; i < queue.length; i++) {
          const [x, z] = queue[i];
          for (const [dx, dz] of [
            [-1, 0],
            [1, 0],
            [0, -1],
            [0, 1],
          ]) {
            const X = x + dx,
              Z = z + dz,
              key = `${X},${Z}`;
            if (
              X < 1 ||
              Z < 1 ||
              X >= building.w - 1 ||
              Z >= building.d - 1 ||
              connected.has(key) ||
              !clear(X, Z) ||
              !clear(x + dx / 2, z + dz / 2)
            )
              continue;
            connected.add(key);
            queue.push([X, Z]);
          }
        }
        // A real kitchenette receives its own reachable reserved work position.
        // Place it in a free back bay; the public graph audit validates the slot.
        for (let x = building.w - 3; x >= 2; x--) {
          const z = building.d - 3,
            approach = floorCells.get(`${x},${z - 1}`);
          if (
            !approach ||
            !connected.has(`${x},${z - 1}`) ||
            places.some(
              (p) =>
                Math.hypot(p.x - (building.x + (x + 0.5) * CELL), p.z - (building.z + (z - 0.5) * CELL)) <
                0.9,
            )
          )
            continue;
          const item = add(x, z, 'kitchen');
          if (!item) continue;
          const place: Place = {
            id: `place:${building.id}:${floor}:kitchen`,
            kind: 'kitchen',
            name: `${building.name} · ${interior.use === 'office' ? 'Tea kitchen' : 'Home kitchen'}`,
            buildingId: building.id,
            floor,
            x: building.x + (x + 0.5) * CELL,
            y,
            z: building.z + (z - 0.5) * CELL,
            capacity: 1,
            duration: 12,
            furnitureId: item.id,
            supportCell: approach.id,
          };
          city.places!.push(place);
          places.push(place);
          break;
        }
        if (interior.use === 'apartment') {
          for (let x = 2; x < building.w - 2; x++) if (add(x, building.d - 2, 'cabinet')) break;
        }
      }
      if (interior.use === 'shop') {
        const clear = roomClearance(building, fixtures, floor, interior.floorHeight);
        for (const counter of places.filter((p) => p.kind === 'counter')) {
          const gx = Math.floor((counter.x - building.x) / CELL),
            gz = Math.floor((counter.z - building.z) / CELL);
          for (const [dx, dz] of [
            [1, 1],
            [-1, 1],
            [1, 0],
            [-1, 0],
            [0, 2],
          ]) {
            const x = gx + dx,
              z = gz + dz,
              X = building.x + (x + 0.5) * CELL,
              Z = building.z + (z + 0.5) * CELL;
            if (
              x < 1 ||
              z < 1 ||
              x >= building.w - 1 ||
              z >= building.d - 1 ||
              !clear(x, z) ||
              places.some((p) => Math.hypot(p.x - X, p.z - Z) < 0.8)
            )
              continue;
            const slot: Place = {
              ...counter,
              id: `staff:${counter.id}`,
              kind: 'staff',
              name: `${building.name} · checkout staff`,
              x: X,
              z: Z,
              supportCell: floorCells.get(`${x},${z}`)!.id,
              duration: 60,
              serves: counter.id,
              facing: Math.atan2(counter.x - X, counter.z + CELL - Z),
            };
            city.places!.push(slot);
            places.push(slot);
            break;
          }
        }
      }
      for (const record of interior.rooms.filter((r) => r.floor === floor)) {
        record.places = places
          .filter(
            (p) =>
              p.x >= record.x && p.x < record.x + record.w && p.z >= record.z && p.z < record.z + record.d,
          )
          .map((p) => p.id);
        record.fixtures = fixtures
          .filter(
            (p) =>
              p.x >= record.x && p.x < record.x + record.w && p.z >= record.z && p.z < record.z + record.d,
          )
          .map((p) => p.id);
        for (const c of floorCells.values())
          if (
            building.x + (c.x + 0.5) * CELL >= record.x &&
            building.x + (c.x + 0.5) * CELL < record.x + record.w
          )
            c.color =
              interior.use === 'apartment'
                ? (building.id + floor) % 2
                  ? 0xc5b294
                  : 0xc9bc9e
                : interior.use === 'office'
                  ? record.x === building.x + CELL
                    ? 0xb5c1b8
                    : 0xbebdac
                  : c.color;
      }
    }
  }
}
